import Button from '@carvana/ds-button';
import React, { useState } from 'react';
import PriceDetails from '|/app/purchase/shared/order-summary/cio';
import SomethingWentWrongModal from '|/components/carvana-ds/something-went-wrong-modal/something-went-wrong-modal';
import currency, { removeZeroCents } from '|/helpers/currency';
import type { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from './pay-at-order.module.css';
import { PriceIsRightContent } from './price-is-right-content';
import type { CompletePurchasePayAtOrderRenderProps } from '../complete-purchase-shell';
import { Consents } from '../components/pay-at-order/consents';
import { DigitalWallets } from '../components/pay-at-order/digital-wallets';
import { ImpersonationBlockedBanner } from '../components/pay-at-order/impersonation-blocked-banner';
import { PlaidModal } from '../components/pay-at-order/plaid-modal';
import { usePayAtOrder } from '../components/pay-at-order/use-pay-at-order';
import {
  isDigitalWalletSelection,
  isPayLaterSelection
} from '../components/pay-at-order/utils/digital-wallet';

const getTreatmentButtonText = (
  amountDue: number,
  isPayLater: boolean
): `Pay ${string} now` | 'Place Order' =>
  amountDue > 0 && !isPayLater ? `Pay ${removeZeroCents(currency(amountDue))} now` : 'Place Order';

const PriceIsRightPayAtOrder = ({
  onSubmit,
  editPaymentMethod,
  paymentSummary,
  reviewSummary
}: CompletePurchasePayAtOrderRenderProps) => {
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);
  const { amountDue } = paymentSummary.purchasePaymentBreakdown;
  const isPayLater = isPayLaterSelection(paymentSummary.paymentSelection);
  const shouldUseDigitalWalletCheckout =
    amountDue > 0 && isDigitalWalletSelection(paymentSummary.paymentSelection);
  const treatmentButtonText = getTreatmentButtonText(amountDue, isPayLater);

  const {
    paymentSelection,
    requestForPaymentAmount,
    isCardEligible,
    isSubmitting,
    isButtonDisabled,
    isPlaidModalOpen,
    isUserBeingImpersonated,
    shouldUseDigitalWallet,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    setHasCustomerConfirmedSufficientFunds,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onCardAdded,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  } = usePayAtOrder({
    onSubmit,
    buttonText: treatmentButtonText,
    useDigitalWalletCheckout: shouldUseDigitalWalletCheckout,
    shouldProcessPayment: amountDue > 0,
    shouldSubmitConsents: amountDue > 0
  });

  return (
    <>
      <div className={styles.container}>
        <PriceIsRightContent
          paymentSummary={paymentSummary}
          reviewSummary={reviewSummary}
          onViewPriceDetails={() => {
            if (reviewSummary) setIsPriceDetailsOpen(true);
          }}
        />

        <div className={styles.actions}>
          {amountDue > 0 && shouldShowConsents && (
            <Consents
              disableClickOnSufficientFundsCheckbox={isSubmitting}
              hasCustomerConfirmedSufficientFunds={hasCustomerConfirmedSufficientFunds}
              setHasCustomerConfirmedSufficientFunds={setHasCustomerConfirmedSufficientFunds}
            />
          )}

          {shouldShowImpersonationText && (
            <ImpersonationBlockedBanner
              shouldUseDigitalWallet={shouldUseDigitalWallet}
              shouldInitiateRequestForPayment={shouldInitiateRequestForPayment}
              selectionType={paymentSelection?.selectionType}
            />
          )}

          {shouldUseDigitalWallet && !isUserBeingImpersonated && (
            <DigitalWallets
              digitalWallet={paymentSelection?.digitalWallet}
              isCardEligible={isCardEligible}
              editPaymentMethod={editPaymentMethod}
              onSupportChange={setIsDigitalWalletSupported}
              onCardAdded={onCardAdded}
              onSetupConfirmed={submitPayAtOrder}
              onError={handleError}
            />
          )}

          {!shouldUseDigitalWallet && (
            <div className={styles.buttonContainer}>
              <Button
                variant="primary"
                size="md"
                width="100%"
                disabled={isButtonDisabled}
                isLoading={isSubmitting}
                onClick={() => {
                  void submitPayAtOrder();
                }}
                data-analytics-id="pp_complete_purchase_pao_submit"
              >
                {treatmentButtonText}
              </Button>
            </div>
          )}
        </div>
      </div>

      <PlaidModal
        isOpen={isPlaidModalOpen}
        onClose={() => {
          setIsPlaidModalOpen(false);
        }}
        paymentAmount={requestForPaymentAmount ?? 0}
        onSuccess={() => {
          handleSubmit().finally(() => {
            setIsPlaidModalOpen(false);
          });
        }}
        onExit={() => {
          setIsPlaidModalOpen(false);
          onPlaidExit();
        }}
        onError={() => {
          setIsPlaidModalOpen(false);
          handleError();
        }}
      />

      <SomethingWentWrongModal text="collecting your payment" />

      {reviewSummary && (
        <PriceDetails
          summary={reviewSummary as ReviewSummaryResponse}
          isOpen={isPriceDetailsOpen}
          setIsOpen={setIsPriceDetailsOpen}
          isCheckoutPage
        />
      )}
    </>
  );
};

export default PriceIsRightPayAtOrder;
